import { zeroPadding } from 'shared/util'
import { SisetuResult } from 'interface/sisetu'
import {
  DashBoardConfigure,
  HorizontalBarGraphFactory,
  VerticalBarGraphFactory,
  ScatterPlotGraphFactory,
  PieGraphFactory,
  MultiPieGraphFactory,
  SunburstGraphFactory,
  TreeMapGraphFactory,
  FunnelGraphFactory
} from 'features/dashBoard'

export const SisetuAnalyticsConfigure: DashBoardConfigure<SisetuResult> = {
  identifier: (sisetu) => sisetu.sisetuCode ? {
    id: sisetu.sisetuCode,
    name: sisetu.sisetuName ?? sisetu.ryakusikiSisetuName ?? '',
  } : undefined,
  graphs: {
    horizonBar: HorizontalBarGraphFactory,
    verticalBar: VerticalBarGraphFactory,
    plot: ScatterPlotGraphFactory,
    pie: PieGraphFactory,
    multiPie: MultiPieGraphFactory,
    sunburst: SunburstGraphFactory,
    treemap: TreeMapGraphFactory,
    funnel: FunnelGraphFactory,
  },
  axis: {
    block: {
      title: 'ブロック',
      val: (sisetu) => sisetu.juusyoBlockCode ? {
        code: sisetu.juusyoBlockCode,
        name: sisetu.juusyoBlockName,
      } : undefined
    },
    todoufuken: {
      title: '都道府県',
      categoryTitle: 'ブロック',
      val: (sisetu) => sisetu.juusyoKenCode ? {
        code: sisetu.juusyoKenCode,
        name: sisetu.juusyoKenName ?? undefined,
        category: sisetu.juusyoBlockCode ? {
          code: sisetu.juusyoBlockCode,
          name: sisetu.juusyoBlockName,
        } : undefined
      } : undefined
    },
    iryouken: {
      title: '医療圏',
      categoryTitle: '都道府県',
      val: (sisetu) => sisetu.iryoukenCode ? {
        code: `${sisetu.iryoukenCode}`,
        name: sisetu.iryoukenName,
        category: sisetu.juusyoKenCode ? {
          code: `${sisetu.juusyoKenCode}`,
          name: sisetu.juusyoKenName ?? undefined
        } : undefined
      } : undefined
    },
    city: {
      title: '市区町村',
      categoryTitle: '医療圏',
      val: (sisetu) => sisetu.cityCode ? {
        code: `${sisetu.cityCode}`,
        name: sisetu.cityName,
        category: sisetu.iryoukenCode ? {
          code: `${sisetu.iryoukenCode}`,
          name: sisetu.iryoukenName
        } : undefined
      } : undefined
    },
    kaigyouStatus: {
      title: '開業状態',
      val: (sisetu) => {
        if (sisetu.heisa) return { code: '9', name: '閉鎖' }
        if (sisetu.kyuuin) return { code: '8', name: '休院' }
        if (sisetu.kaigyouYotei) return { code: '7', name: '開業予定' }
        return { code: '1', name: '開業中' }
      },
    },
    keieitaiCategory: {
      title: '経営体(大項目)',
      val: (sisetu) => sisetu.keieitai?.categoryCode ? {
        code: sisetu.keieitai.categoryCode,
        name: sisetu.keieitai.categoryName,
      } : undefined
    },
    keieitai: {
      title: '経営体(詳細)',
      categoryTitle: '大項目',
      val: (sisetu) => sisetu.keieitai?.code ? {
        code: sisetu.keieitai.code,
        name: sisetu.keieitai.shortName ?? sisetu.keieitai.name,
        category: sisetu.keieitai?.categoryCode ? {
          code: sisetu.keieitai.categoryCode,
          name: sisetu.keieitai.categoryName
        } : undefined
      } : undefined
    },
    sisetuKubunCategory: {
      title: '施設区分(大項目)',
      val: (sisetu) => sisetu.sisetuKubun?.categoryCode ? {
        code: sisetu.sisetuKubun.categoryCode,
        name: sisetu.sisetuKubun.categoryName,
      } : undefined
    },
    sisetuKubun: {
      title: '施設区分(詳細)',
      categoryTitle: '大項目',
      val: (sisetu) => sisetu.sisetuKubun?.code ? {
        code: sisetu.sisetuKubun.code,
        name: sisetu.sisetuKubun.name,
        category: sisetu.sisetuKubun.categoryCode ? {
          code: sisetu.sisetuKubun.categoryCode,
          name: sisetu.sisetuKubun.categoryName,
        } : undefined
      } : undefined
    },
    byouinSyubetu: {
      title: '病院種別',
      val: (sisetu) => sisetu.byouinSyubetu ? {
        code: sisetu.byouinSyubetu.code,
        name: sisetu.byouinSyubetu.shortName ?? sisetu.byouinSyubetu.name,
      } : undefined
    },
    bedCount: {
      title: '病床数',
      val: (sisetu) => 
        sisetu.bedCount === undefined || sisetu.bedCount.total === null ? { code: 'N', name: '無床' } :
        sisetu.bedCount.total >= 600 ? { code: '1', name: '600床以上' } :
        sisetu.bedCount.total >= 400 ? { code: '2', name: '400～599床' } :
        sisetu.bedCount.total >= 200 ? { code: '3', name: '200～399床' } :
        sisetu.bedCount.total >= 100 ? { code: '4', name: '100～199床' } :
        sisetu.bedCount.total >= 20 ? { code: '5', name: '20～99床' } :
        sisetu.bedCount.total > 0 ? { code: '6', name: '有床～19床' } : { code: 'N', name: '無床' }
    },
    kensa: {
      title: '検査',
      val: (sisetu) => {
        const list = []
        if (sisetu.kensa) {
          if (sisetu.kensa.biseibutu) list.push({ code: '01', name: '微生物検査' })
          if (sisetu.kensa.kessei) list.push({ code: '02', name: '血清検査' })
          if (sisetu.kensa.ketueki) list.push({ code: '03', name: '血液検査' })
          if (sisetu.kensa.byouri) list.push({ code: '04', name: '病理検査' })
          if (sisetu.kensa.kiseichuu) list.push({ code: '05', name: '寄生虫検査' })
          if (sisetu.kensa.seika) list.push({ code: '06', name: '生化学検査' })
          if (sisetu.kensa.ri) list.push({ code: '07', name: 'RI検査' })
        }
        return list
      },
    },
    hospitalFlag: {
      title: '病院機能',
      val: (sisetu) => sisetu.hospitalFlags?.map(flag => ({
        code: zeroPadding(flag.functionId, 3),
        name: flag.functionName
      }))
    },
  },
  value: {
    dpc: {
      title: 'DPCスコア',
      val: (sisetu) => sisetu.score?.dpc?.score,
      minValue: 0,
      unit: 'pt',
    },
    cancer: {
      title: 'がんスコア',
      val: (sisetu) => sisetu.score?.cancer?.score,
      minValue: 0,
      unit: 'pt',
    },
    trial: {
      title: '治験スコア',
      val: (sisetu) => sisetu.score?.trial?.score,
      minValue: 0,
      unit: 'pt',
    },
    journal: {
      title: '論文スコア',
      val: (sisetu) => sisetu.score?.journal?.score,
      minValue: 0,
      unit: 'pt',
    },
    author: {
      title: '論文著者数',
      val: (sisetu) => sisetu.score?.journal?.authorsCount,
      minValue: 0,
      unit: '人',
    },
    article: {
      title: '論文数',
      val: (sisetu) => sisetu.score?.journal?.articleCount,
      minValue: 0,
      unit: '',
    },
    senmoni: {
      title: '専門医スコア',
      val: (sisetu) => sisetu.score?.senmoni?.score,
      minValue: 0,
      unit: 'pt',
    },
    bedCount: {
      title: '病床数',
      val: (sisetu) => sisetu.bedCount?.total ?? undefined,
      minValue: 0,
      unit: '床',
    },
    bedCountIppan: {
      title: '一般病床数',
      val: (sisetu) => sisetu.bedCount?.ippan ?? undefined,
      minValue: 0,
      unit: '床',
    },
    kyukyuHolidayNumberOfPatients: {
      title: '休日診療(年間)',
      val: (sisetu) => sisetu.ambulance?.kyukyuHolidayNumberOfPatients ?? undefined,
      minValue: 0,
      unit: '人',
    },
    kyukyuNighttimeNumberOfPatients: {
      title: '夜間/時間外(年間)',
      val: (sisetu) => sisetu.ambulance?.kyukyuNighttimeNumberOfPatients ?? undefined,
      minValue: 0,
      unit: '人',
    },
    numberOfAmbulanceAcceptance: {
      title: '救急受入(年間)',
      val: (sisetu) => sisetu.ambulance?.numberOfAmbulanceAcceptance ?? undefined,
      minValue: 0,
      unit: '人',
    },
    kojinCount: {
      title: '医師数',
      val: (sisetu) => sisetu.kojinCount,
      minValue: 0,
      unit: '人',
    },
    pidCount: {
      title: 'ユーザー数',
      val: (sisetu) => sisetu.pidCount,
      minValue: 0,
      unit: '人',
    },
    count: {
      title: '施設数',
      val: () => 1,
      minValue: 0,
      unit: '施設',
    }
  }
}
